import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";

import { Chip } from "../../chips";
import { OptionLink } from "../../links";
import { CellBar } from "../bars";
import type { PageProps } from "../index";
import { MUTED_ROW, OptionFlags, PageHead } from "../kit";
import { RowsPage, TruncatedNote, type RowsData } from "../rows";

export interface CompletionRow {
  option_id: string;
  option_name: string;
  item_id: string;
  item_name: string;
  phase: string | null;
  dropped: boolean;
  overall_rating: number | null;
  not_scored: boolean;
  low_evidence: boolean;
  criteria_phase: string;
  expected_n: number;
  scored_n: number;
  completion: number | null;
  missing_n: number;
}

export interface CompletionCell {
  percent: number;
  missing: number;
  reached: boolean;
}

export interface CompletionOption {
  option_id: string;
  option_name: string;
  item_id: string;
  phase: string | null;
  dropped: boolean;
  not_scored: boolean;
  low_evidence: boolean;
  cells: Record<string, CompletionCell>;
  /** Share of criteria scored over the phases the option has reached, or null when there are none. */
  totalReached: number | null;
}

/** "2: In-Depth Screen" reads as 2. */
export function phaseNo(phase: string | null): number {
  const parsed = Number.parseInt(phase ?? "", 10);
  return Number.isNaN(parsed) ? 1 : parsed;
}

/** How far an option has got through the three criteria phases. Archived or phase-less options count as phase 1. */
export function reachedNo(optionPhase: string | null): number {
  if (optionPhase === null || optionPhase === "ARCHIVE") return 1;
  return Math.min(3, phaseNo(optionPhase));
}

/** One line per option, one column per criteria phase. A cut-off last option is left out. */
export function completionTable(
  rows: CompletionRow[],
  truncated: boolean,
): { phases: Array<{ phase: string; expected: number }>; options: CompletionOption[] } {
  const expected = new Map<string, number>();
  const options: CompletionOption[] = [];
  const byId = new Map<string, CompletionOption>();
  const sums = new Map<string, { scored: number; expected: number }>();
  for (const row of rows) {
    let option = byId.get(row.option_id);
    if (!option) {
      option = {
        option_id: row.option_id,
        option_name: row.option_name,
        item_id: row.item_id,
        phase: row.phase,
        dropped: row.dropped,
        not_scored: row.not_scored,
        low_evidence: row.low_evidence,
        cells: {},
        totalReached: null,
      };
      byId.set(row.option_id, option);
      options.push(option);
      sums.set(row.option_id, { scored: 0, expected: 0 });
    }
    const reached = phaseNo(row.criteria_phase) <= reachedNo(row.phase);
    option.cells[row.criteria_phase] = {
      percent: Math.round((row.completion ?? 0) * 100),
      missing: row.missing_n,
      reached,
    };
    if (reached) {
      const sum = sums.get(row.option_id)!;
      sum.scored += Math.min(row.scored_n, row.expected_n);
      sum.expected += row.expected_n;
    }
  }
  for (const option of options) {
    const sum = sums.get(option.option_id)!;
    option.totalReached = sum.expected > 0 ? Math.round((sum.scored / sum.expected) * 100) : null;
  }
  if (truncated && options.length > 1) options.pop();
  const kept = new Set(options.map((option) => option.option_id));
  for (const row of rows) {
    if (kept.has(row.option_id) && !expected.has(row.criteria_phase)) expected.set(row.criteria_phase, row.expected_n);
  }
  const phases = Array.from(expected, ([phase, count]) => ({ phase, expected: count })).sort((a, b) => a.phase.localeCompare(b.phase));
  return { phases, options };
}

export function completionColumns(phases: Array<{ phase: string; expected: number }>): ColumnDef<CompletionOption, unknown>[] {
  return [
    {
      id: "option",
      header: "Option",
      cell: ({ row }) => (
        <span className="flex flex-wrap items-center gap-1">
          <OptionLink optionId={row.original.option_id} itemId={row.original.item_id} from="criteria_completion_phase">
            {row.original.option_name}
          </OptionLink>
          <OptionFlags row={row.original} />
        </span>
      ),
    },
    ...phases.map(
      ({ phase, expected }): ColumnDef<CompletionOption, unknown> => ({
        id: `phase:${phase}`,
        header: `${phase} (${expected})`,
        meta: { className: "min-w-44" },
        cell: ({ row }) => {
          const cell = row.original.cells[phase];
          if (!cell) return "—";
          if (!cell.reached) return <Chip tone="muted">Not reached</Chip>;
          return (
            <>
              <div className="tabular-nums">
                <b>{cell.percent}%</b>{" "}
                {cell.missing > 0 ? <span className="text-xs text-ks-danger">{cell.missing} missing</span> : null}
              </div>
              <CellBar percent={cell.percent} />
            </>
          );
        },
      }),
    ),
    {
      id: "total",
      header: "Total reached",
      meta: { align: "right" },
      cell: ({ row }) => <b>{row.original.totalReached === null ? "—" : `${row.original.totalReached}%`}</b>,
    },
  ];
}

export function CriteriaCompletionBody({ data }: { data: RowsData<CompletionRow> }) {
  const { phases, options } = completionTable(data.rows, data.truncated);
  return (
    <>
      <div className="overflow-x-auto">
        <DataTable<CompletionOption>
          columns={completionColumns(phases)}
          rows={options}
          page={{ pageIndex: 0, pageSize: Math.max(1, options.length), rowCount: options.length }}
          onPageChange={() => {}}
          density="catalogue"
          caption="Share of each phase's criteria scored, per option"
          rowClassName={(option) => (option.dropped ? MUTED_ROW : undefined)}
        />
      </div>
      {data.truncated ? <TruncatedNote shown={options.length} /> : null}
    </>
  );
}

export function CriteriaCompletionPage({ tab, areaName }: PageProps) {
  return (
    <RowsPage<CompletionRow>
      tab={tab}
      areaName={areaName}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={data?.rows[0] ? `${data.rows[0].item_name} · share of each phase's criteria scored, per option` : undefined}
        />
      )}
    >
      {(data) => <CriteriaCompletionBody data={data} />}
    </RowsPage>
  );
}
